import React from 'react';
import { useSearchParams } from 'react-router-dom';

import ButtonLink from '@dashboard/components/mui-atoms/ButtonLink';
import TableHeader, { type Props as TableHeaderProps } from '@dashboard/components/table/TableHeader';
import { useEmailMessagesListHref } from '@dashboard/hooks/route/hrefs';
import { useSetCurrentPage } from '@dashboard/hooks/route/useCurrentPage';
import { CalendarViewDayOutlined, HistoryOutlined, TableRowsOutlined } from '@mui/icons-material';
import { Box, Stack, Tab, Tabs, Typography } from '@mui/material';

import CreateTemplateButton from './CreateTemplateButton';
import { useCurrentTemplatesQuery } from './hooks';

const DEFAULT_VALUE = 'TEMPLATE';
function castTypeValue(v: unknown) {
  switch (v) {
    case 'LAYOUT':
    case 'TEMPLATE':
      return v;
    default:
      return DEFAULT_VALUE;
  }
}

export default function PageHeader() {
  const { data } = useCurrentTemplatesQuery();

  const [searchParams, setSearchParams] = useSearchParams();
  const type = castTypeValue(searchParams.get('type'));

  const handleTypeChange = (_: unknown, v: unknown) => {
    setSearchParams((prevParams) => {
      const value = castTypeValue(v);
      if (value === DEFAULT_VALUE) {
        prevParams.delete('type');
      } else {
        prevParams.set('type', value);
      }
      return prevParams;
    });
  };

  const setPage = useSetCurrentPage();
  const emailMessagesHref = useEmailMessagesListHref();

  const props: TableHeaderProps = {
    setPage,
    title: 'Templates',
    titleActions: (
      <Box>
        <Tabs value={type} onChange={handleTypeChange} sx={{ my: -1, '& .MuiTab-root': { height: 52 } }}>
          <Tab
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <CalendarViewDayOutlined sx={{ color: 'grey.600' }} />
                <Typography fontWeight="medium" sx={{ display: { xs: 'none', md: 'block' } }}>
                  Templates
                </Typography>
              </Stack>
            }
            value="TEMPLATE"
          />
          <Tab
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <TableRowsOutlined sx={{ color: 'grey.600' }} />
                <Typography fontWeight="medium" sx={{ display: { xs: 'none', md: 'block' } }}>
                  Layouts
                </Typography>
              </Stack>
            }
            value="LAYOUT"
          />
        </Tabs>
      </Box>
    ),
    actions: (
      <Stack direction="row" spacing={2} alignItems="center">
        <ButtonLink
          size="small"
          href={emailMessagesHref}
          color="secondary"
          startIcon={<HistoryOutlined fontSize="small" />}
          sx={{ display: { xs: 'none', md: 'flex' } }}
        >
          Send history
        </ButtonLink>

        <CreateTemplateButton />
      </Stack>
    ),
  };

  const res = data?.WaypointTemplates;
  if (!res) {
    return <TableHeader {...props} />;
  }

  const count = res.count;
  const limit = res.meta.limit;
  const currentPage = res.meta.page;
  return <TableHeader currentPage={currentPage} limit={limit} count={count} {...props} />;
}
